import { ReactNode, useCallback, useEffect, useState } from 'react'

import { MeetingsRoomProviderDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'

import { useMeetingSoundsSettings } from 'modules/meetings/lib/hooks/useMeetingSounds'

import { RoomContext, type RoomContextState, type RoomContextType } from '../RoomContext'

interface RoomProviderProps {
  children: ReactNode
}

export const RoomProvider = ({ children }: RoomProviderProps) => {
  const [meetingId, setMeetingId] = useState<number | null>(null)
  // Room data states
  const [room, setRoom] = useState<RoomContextState['room']>(null)
  const [isSideBarOpen, setIsSideBarOpen] = useState(true)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  // Loading and error states
  const [error, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Pending messages state
  const [pendingMessagesCount, setPendingMessagesCount] = useState(0)

  // Meeting sounds settings
  const { enabled: soundsEnabled, setEnabled: setSoundsEnabled } = useMeetingSoundsSettings()

  const [getOrCreateRoom, { loading: roomLoading }] = useMutation(MeetingsRoomProviderDocument, {
    extractorFn: (data) => ({
      ...data.meetingsMutations.getOrCreateMeetingsRoom.room,
      errors: data.meetingsMutations.getOrCreateMeetingsRoom.errors || [],
    }),
  })

  useEffect(() => {
    if (!roomLoading && room) {
      setIsLoading(false)
    }
  }, [room, roomLoading])

  const fetchRoom = useCallback(async () => {
    if (!meetingId) {
      setError(true)
      setErrorMessage('Meeting ID is required')
      return
    }

    try {
      const result = await getOrCreateRoom({
        variables: { meetingId: Number(meetingId) },
      })

      const handled = handleResult(result, () => undefined)

      if (handled.success && handled.data) {
        const roomData: RoomContextType = {
          id: handled.data.id || undefined,
          roomName: handled.data.roomName || undefined,
          roomSid: handled.data.roomSid || undefined,
          serverUrl: handled.data.serverUrl || undefined,
          token: handled.data.token || undefined,
        }

        if (roomData?.id && roomData?.token && roomData?.roomName) {
          setRoom(roomData)
          setError(false)
          setErrorMessage(null)
        } else {
          setError(true)
          setErrorMessage('Room data is incomplete')
        }
      } else {
        setError(true)
        setErrorMessage('Failed to create or get room')
      }
    } catch (err) {
      setError(true)
      setErrorMessage(err instanceof Error ? err.message : 'Unknown error occurred')
    }
  }, [getOrCreateRoom, meetingId])

  const resetError = () => {
    setError(false)
    setErrorMessage(null)
  }

  useEffect(() => {
    if (meetingId) {
      fetchRoom()
    }
  }, [meetingId, fetchRoom])

  const addPendingMessage = useCallback(() => {
    setPendingMessagesCount((prev) => prev + 1)
  }, [])

  const clearPendingMessages = useCallback(() => {
    setPendingMessagesCount(0)
  }, [])

  const value: RoomContextState = {
    meetingId,
    setMeetingId,
    isSideBarOpen,
    setIsSideBarOpen: (state: boolean) => {
      setIsSideBarOpen(state)
      setIsChatOpen(false)
    },
    isChatOpen,
    setIsChatOpen: (state: boolean) => {
      setIsChatOpen(state)
      setIsSideBarOpen(false)
      // Clear pending messages when chat is opened
      if (state) {
        clearPendingMessages()
      }
    },
    room,
    loading: isLoading,
    error,
    errorMessage,
    refetchRoom: fetchRoom,
    resetError,
    soundsEnabled: soundsEnabled ?? true,
    setSoundsEnabled,
    pendingMessagesCount,
    addPendingMessage,
    clearPendingMessages,
  }

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>
}
